import React from 'react';
import styles from './styles/Pagination.module.css';

export interface PaginationMeta {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
}

/** Respuesta paginada del backend: { items, pagination }. */
export interface PaginatedResponse<T> {
    items: T[];
    pagination: PaginationMeta;
}

export const EMPTY_PAGINATION: PaginationMeta = {
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
};

/**
 * Normaliza la respuesta de un endpoint que puede venir paginado o como
 * array plano (los endpoints mantienen ambos formatos por compatibilidad).
 */
export function readPaginated<T>(data: unknown): PaginatedResponse<T> {
    if (Array.isArray(data)) {
        return {
            items: data as T[],
            pagination: { ...EMPTY_PAGINATION, total: data.length, total_pages: 1 },
        };
    }
    const payload = data as Partial<PaginatedResponse<T>> | null;
    if (payload && Array.isArray(payload.items)) {
        return {
            items: payload.items,
            pagination: { ...EMPTY_PAGINATION, ...(payload.pagination || {}) },
        };
    }
    return { items: [], pagination: EMPTY_PAGINATION };
}

interface PaginationProps {
    meta: PaginationMeta;
    onPageChange: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
    perPageOptions?: number[];
    /** Nombre de lo que se lista, para el resumen ("pedidos", "productos"). */
    itemLabel?: string;
}

const PER_PAGE_OPTIONS = [25, 50, 100, 200];

const Pagination: React.FC<PaginationProps> = ({
    meta,
    onPageChange,
    onPerPageChange,
    perPageOptions = PER_PAGE_OPTIONS,
    itemLabel = 'registros',
}) => {
    const { page, per_page, total, total_pages, has_next, has_prev } = meta;

    if (total === 0) return null;

    const from = (page - 1) * per_page + 1;
    const to = Math.min(page * per_page, total);

    return (
        <div className={styles.pagination}>
            <span className={styles.summary}>
                {from}–{to} de {total} {itemLabel}
            </span>

            <div className={styles.controls}>
                {onPerPageChange && (
                    <label className={styles.perPage}>
                        <span>Por pagina</span>
                        <select
                            value={per_page}
                            onChange={e => onPerPageChange(Number(e.target.value))}
                        >
                            {perPageOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </label>
                )}

                <div className={styles.pager}>
                    <button
                        type="button"
                        onClick={() => onPageChange(page - 1)}
                        disabled={!has_prev}
                        aria-label="Pagina anterior"
                    >
                        Anterior
                    </button>
                    <span className={styles.pageInfo}>
                        Pagina {page} de {total_pages || 1}
                    </span>
                    <button
                        type="button"
                        onClick={() => onPageChange(page + 1)}
                        disabled={!has_next}
                        aria-label="Pagina siguiente"
                    >
                        Siguiente
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Pagination;
